// Editor.js block tool for embedding a form built on the portal's Forms page.
// Stores { formId } — the published site resolves the id against the build
// feed's forms and renders the real accessible form (CustomForm.astro), so the
// editor shows a read-only preview of the field list.
//
// config: { listForms }
//   listForms() -> { forms: [{ id, name, fields, submission_count }] }

const ICON =
  '<svg width="17" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="4" rx="1"/><rect x="3" y="11" width="18" height="4" rx="1"/><path d="M3 20h8"/></svg>';

const PREVIEW_LIMIT = 6;

export class FormTool {
  static get toolbox() {
    return { title: "Form", icon: ICON };
  }

  constructor({ data, config, block }) {
    this.block = block;
    this.config = config || {};
    this.data = {
      formId: typeof data?.formId === "string" ? data.formId : "",
    };
    this._forms = [];
    this._loaded = false;
  }

  render() {
    const root = document.createElement("div");
    root.className = "pgs-form-tool";

    const bar = document.createElement("div");
    bar.className = "pgs-form-tool__bar";
    const title = document.createElement("span");
    title.className = "pgs-form-tool__title";
    title.textContent = "Form";

    // Form picker — populated once the list loads.
    const select = document.createElement("select");
    select.className = "pgs-form-tool__select";
    select.setAttribute("aria-label", "Form");
    select.addEventListener("change", () => {
      this.data.formId = select.value;
      this._renderPreview();
      this.block?.dispatchChange?.();
    });
    this._select = select;

    bar.append(title, select);

    const preview = document.createElement("div");
    preview.className = "pgs-form-tool__preview";
    this._preview = preview;

    root.append(bar, preview);

    this._loadForms();

    return root;
  }

  async _loadForms() {
    this._preview.textContent = "Loading forms…";
    try {
      const data = await this.config.listForms?.();
      this._forms = data?.forms ?? [];
    } catch (err) {
      this._preview.textContent = `Couldn't load forms: ${err.message}`;
      return;
    }
    this._loaded = true;
    this._renderSelect();
    this._renderPreview();
  }

  _renderSelect() {
    const select = this._select;
    select.innerHTML = "";

    const placeholder = document.createElement("option");
    placeholder.value = "";
    placeholder.textContent = this._forms.length ? "Choose a form…" : "No forms yet";
    select.appendChild(placeholder);

    for (const form of this._forms) {
      const option = document.createElement("option");
      option.value = form.id;
      option.textContent = `${form.name} (${form.fields.length} field${form.fields.length === 1 ? "" : "s"})`;
      select.appendChild(option);
    }

    // A stored id whose form was deleted: keep it selectable so the block
    // isn't stuck, but flag it.
    const missing = this.data.formId && !this._forms.some((f) => f.id === this.data.formId);
    if (missing) {
      const option = document.createElement("option");
      option.value = this.data.formId;
      option.textContent = "(form deleted)";
      select.appendChild(option);
    }

    select.value = this.data.formId;
  }

  _renderPreview() {
    const preview = this._preview;
    preview.innerHTML = "";

    if (!this.data.formId) {
      preview.textContent = this._forms.length
        ? "Choose a form to show here."
        : "No forms yet — build one on the Forms page first.";
      return;
    }

    const form = this._forms.find((f) => f.id === this.data.formId);
    if (!form) {
      preview.textContent = "This form no longer exists — choose another.";
      return;
    }
    if (!form.fields.length) {
      preview.textContent = `"${form.name}" has no fields yet — add some on the Forms page.`;
      return;
    }

    const list = document.createElement("ul");
    list.className = "pgs-form-tool__fields";
    for (const field of form.fields.slice(0, PREVIEW_LIMIT)) {
      const item = document.createElement("li");
      item.textContent = `${field.label}${field.required ? " *" : ""}`;
      list.appendChild(item);
    }
    const summary = document.createElement("p");
    summary.className = "pgs-form-tool__summary";
    const extra = form.fields.length - PREVIEW_LIMIT;
    summary.textContent =
      `Your live "${form.name}" form appears here on the published page` +
      (extra > 0 ? ` (${extra} more field${extra === 1 ? "" : "s"} not shown)` : "") +
      ".";
    preview.append(list, summary);
  }

  save() {
    return { formId: this.data.formId };
  }
}
